import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot, faSpinner, faCheckCircle, faTimesCircle,
  faExclamationTriangle, faCookie, faGlobe, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import proxyService from '../../services/proxyService';

const CloudflareSolver = ({ sessionId, webTarget, isDarkMode, onCookiesImported }) => {
  const [solving, setSolving] = useState(false);
  const [progress, setProgress] = useState(null);
  const [targetUrl, setTargetUrl] = useState('');
  const [showProgress, setShowProgress] = useState(false);

  // Auto-fill target URL from web target
  useEffect(() => {
    if (webTarget?.url) {
      setTargetUrl(webTarget.url);
    }
  }, [webTarget]);

  // Poll for progress updates
  useEffect(() => {
    if (!solving) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await proxyService.getCloudflareSolverProgress(sessionId);
        setProgress(response.data);

        // Check if complete
        if (response.data.status === 'success') {
          setSolving(false);
          setShowProgress(true);

          // Show success toast with cookie count
          toast.success(
            `Cloudflare bypassed! ${response.data.cookies?.length || 0} cookies injected`,
            {
              position: 'top-right',
              autoClose: 5000,
              icon: <FontAwesomeIcon icon={faCookie} />
            }
          );

          // Trigger Cookie Manager refresh
          if (onCookiesImported) {
            onCookiesImported(response.data.cookies);
          }

          // Keep progress visible for a bit
          setTimeout(() => setShowProgress(false), 10000);
        } else if (response.data.status === 'error') {
          setSolving(false);
          setShowProgress(true);

          toast.error(
            `Cloudflare solver failed: ${response.data.error}`,
            {
              position: 'top-right',
              autoClose: 8000
            }
          );
        }
      } catch (error) {
        console.error('Failed to get progress:', error);
      }
    }, 1000); // Poll every second

    return () => clearInterval(pollInterval);
  }, [solving, sessionId]);

  const handleSolve = async () => {
    if (!targetUrl) {
      toast.error('Please enter a target URL');
      return;
    }

    try {
      setSolving(true);
      setShowProgress(true);
      setProgress({
        status: 'starting',
        message: 'Starting Cloudflare solver...',
        progress: 0
      });

      await proxyService.startCloudflareSolver(sessionId, targetUrl);

      toast.info('Cloudflare solver started...', {
        position: 'top-right',
        autoClose: 2000
      });
    } catch (error) {
      console.error('Failed to start solver:', error);
      setSolving(false);
      setShowProgress(false);

      toast.error(`Failed to start solver: ${error.message}`, {
        position: 'top-right',
        autoClose: 5000
      });
    }
  };

  const getStatusIcon = () => {
    if (!progress) return faRobot;

    switch (progress.status) {
      case 'starting':
      case 'launching':
      case 'navigating':
      case 'detecting':
      case 'solving':
        return faSpinner;
      case 'success':
      case 'solved':
        return faCheckCircle;
      case 'error':
        return faTimesCircle;
      case 'no_challenge':
        return faExclamationTriangle;
      default:
        return faRobot;
    }
  };

  const getStatusColor = () => {
    if (!progress) return 'text-blue-500';

    switch (progress.status) {
      case 'success':
      case 'solved':
        return 'text-green-500';
      case 'error':
        return 'text-red-500';
      case 'no_challenge':
        return 'text-yellow-500';
      default:
        return 'text-blue-500';
    }
  };

  const isSpinning = progress && [
    'starting',
    'launching',
    'navigating',
    'detecting',
    'solving'
  ].includes(progress.status);

  return (
    <div className={`p-3 rounded border ${
      isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-blue-50 border-blue-200'
    }`}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <FontAwesomeIcon
          icon={faShieldAlt}
          className="text-purple-500"
        />
        <span className={`font-medium text-sm ${
          isDarkMode ? 'text-gray-200' : 'text-gray-800'
        }`}>
          Cloudflare Auto-Solver
        </span>
      </div>

      <p className={`text-xs mb-3 ${
        isDarkMode ? 'text-gray-400' : 'text-gray-600'
      }`}>
        Automatically bypass Cloudflare challenges using headless browser with stealth mode
      </p>

      {/* Target URL Input */}
      <div className="mb-3">
        <label className={`block text-xs mb-1 ${
          isDarkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>
          <FontAwesomeIcon icon={faGlobe} className="mr-1" />
          Target URL
        </label>
        <input
          type="url"
          value={targetUrl}
          onChange={(e) => setTargetUrl(e.target.value)}
          placeholder="https://www.netsolution.ch"
          disabled={solving}
          className={`w-full px-3 py-2 text-sm rounded border ${
            isDarkMode
              ? 'bg-gray-800 text-gray-200 border-gray-600'
              : 'bg-white text-gray-800 border-gray-300'
          } focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50`}
        />
      </div>

      {/* Solve Button */}
      <button
        onClick={handleSolve}
        disabled={solving || !targetUrl}
        className={`w-full px-4 py-2 rounded font-medium text-sm transition-colors ${
          solving || !targetUrl
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-purple-600 hover:bg-purple-700 text-white'
        }`}
      >
        {solving ? (
          <>
            <FontAwesomeIcon icon={faSpinner} className="mr-2" spin />
            Solving...
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faRobot} className="mr-2" />
            Auto-Solve Cloudflare
          </>
        )}
      </button>

      {/* Progress Panel */}
      {showProgress && progress && (
        <div className={`mt-3 p-3 rounded border ${
          isDarkMode
            ? 'bg-gray-800 border-gray-600'
            : 'bg-white border-gray-300'
        }`}>
          {/* Status */}
          <div className="flex items-center gap-2 mb-2">
            <FontAwesomeIcon
              icon={getStatusIcon()}
              className={`${getStatusColor()} ${isSpinning ? 'fa-spin' : ''}`}
            />
            <span className={`text-sm font-medium ${
              isDarkMode ? 'text-gray-200' : 'text-gray-800'
            }`}>
              {progress.message || 'Processing...'}
            </span>
          </div>

          {/* Progress Bar */}
          {progress.progress > 0 && (
            <div className={`w-full h-2 rounded-full overflow-hidden mb-2 ${
              isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
            }`}>
              <div
                className={`h-full transition-all duration-300 ${
                  progress.status === 'success'
                    ? 'bg-green-500'
                    : progress.status === 'error'
                    ? 'bg-red-500'
                    : 'bg-purple-500'
                }`}
                style={{ width: `${progress.progress}%` }}
              />
            </div>
          )}

          {/* Extracted Cookies */}
          {progress.cookies && progress.cookies.length > 0 && (
            <div className={`mt-3 p-2 rounded border ${
              isDarkMode
                ? 'bg-gray-900 border-gray-700'
                : 'bg-gray-50 border-gray-300'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon={faCookie} className="text-yellow-500" />
                <span className={`text-xs font-medium ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Extracted Cookies ({progress.cookies.length})
                </span>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {progress.cookies.map((cookie, index) => (
                  <div
                    key={index}
                    className={`px-2 py-1 rounded text-xs font-mono ${
                      isDarkMode
                        ? 'bg-gray-800 text-gray-300'
                        : 'bg-white text-gray-700'
                    }`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <span className="font-semibold text-yellow-500 truncate">
                        {cookie.name}
                      </span>
                      <span className={`text-[10px] ${
                        isDarkMode ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        {cookie.domain}
                      </span>
                    </div>
                    <div className={`mt-0.5 text-[10px] break-all ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {cookie.value.length > 100
                        ? cookie.value.substring(0, 100) + '...'
                        : cookie.value}
                    </div>
                  </div>
                ))}
              </div>
              <div className={`mt-2 text-xs ${
                isDarkMode ? 'text-green-400' : 'text-green-600'
              }`}>
                ✓ Cookies saved to Cookie Manager
              </div>
            </div>
          )}

          {/* Screenshot Preview */}
          {progress.screenshot && (
            <div className="mt-3">
              <p className={`text-xs mb-1 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Final page screenshot:
              </p>
              <img
                src={progress.screenshot}
                alt="Solved page"
                className="w-full rounded border border-gray-600 cursor-pointer hover:opacity-80"
                onClick={() => window.open(progress.screenshot, '_blank')}
              />
            </div>
          )}

          {/* Error Details */}
          {progress.error && (
            <div className={`mt-2 p-2 rounded text-xs ${
              isDarkMode
                ? 'bg-red-900/30 text-red-400'
                : 'bg-red-100 text-red-700'
            }`}>
              {progress.error}
            </div>
          )}
        </div>
      )}

      {/* Info */}
      <div className={`mt-3 p-2 rounded text-xs ${
        isDarkMode
          ? 'bg-purple-900/30 text-purple-300'
          : 'bg-purple-100 text-purple-700'
      }`}>
        <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
        Cookies will be automatically injected into proxy requests after solving
      </div>
    </div>
  );
};

export default CloudflareSolver;
